import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTL, fmtInt, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge, StatusBadge } from "@/components/StatusBadge";
import { Edit, ArrowLeft, Package, ChevronLeft, ChevronRight } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [movements, setMovements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => navigate("/products"));
    api.get(`/stock/movements`, { params: { product_id: id } }).then((r) => setMovements(r.data));
    api.get("/definitions/categories").then((r) => setCategories(r.data));
    api.get("/definitions/brands").then((r) => setBrands(r.data));
  }, [id, navigate]);

  if (!product) return <div className="text-[#6B7280]">Yükleniyor...</div>;

  const cat = categories.find((c) => c.id === product.category_id);
  const brand = brands.find((b) => b.id === product.brand_id);
  const canSeePrice = user?.role !== "sales";
  const canEdit = ["admin", "warehouse"].includes(user?.role);

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/products")} data-testid="back-products"
                  className="p-2 rounded-lg hover:bg-white">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2 text-xs text-[#6B7280] font-semibold uppercase tracking-wider">
              <Package size={12} /> {product.product_code}
            </div>
            <h2 className="text-2xl font-extrabold text-[#08285A]">{product.product_name}</h2>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => navigate(`/products/${id}/edit`)} data-testid="edit-from-detail"
                  className="bg-[#0B3676] hover:bg-[#08285A] text-white">
            <Edit size={14} className="mr-1.5" /> Düzenle
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="azd-card p-5 lg:col-span-2">
          {product.images?.length > 0 && (() => {
            const BACKEND = process.env.REACT_APP_BACKEND_URL;
            const tok = localStorage.getItem("azd_token");
            const cur = product.images[imgIdx % product.images.length];
            return (
              <div className="mb-5 relative bg-[#F6F8FB] rounded-lg overflow-hidden border border-[#E5E7EB]">
                <img src={`${BACKEND}${cur.download_url}?auth=${tok}`}
                     alt={cur.original_filename}
                     className="w-full h-72 object-contain" />
                {product.images.length > 1 && (
                  <>
                    <button onClick={() => setImgIdx((i) => i - 1 < 0 ? product.images.length - 1 : i - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow">
                      <ChevronLeft size={18} />
                    </button>
                    <button onClick={() => setImgIdx((i) => (i + 1) % product.images.length)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow">
                      <ChevronRight size={18} />
                    </button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-bold">
                      {(imgIdx % product.images.length) + 1} / {product.images.length}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">
            Ürün Bilgileri
          </div>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Info label="Kategori" value={cat?.name} />
            <Info label="Marka" value={brand?.name} />
            <Info label="Birim" value={product.unit} />
            <Info label="Kaynak" value={product.source_type === "production" ? "Üretim" : "Hazır Alım"} />
            <Info label="Tedarikçi" value={product.supplier_name || "-"} />
            <Info label="Durum" valueEl={<StatusBadge status={product.is_active ? "approved" : "cancelled"} />} />
          </div>
          {product.description && (
            <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
              <div className="text-xs text-[#6B7280] uppercase tracking-wider font-bold mb-1">
                Açıklama
              </div>
              <div className="text-sm text-[#111827] whitespace-pre-line">{product.description}</div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="azd-card azd-stat-card p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
              Mevcut Stok
            </div>
            <div className="text-3xl font-extrabold text-[#08285A] mt-1">
              {fmtInt(product.current_stock)} <span className="text-base text-[#6B7280] font-semibold">{product.unit}</span>
            </div>
            <div className="text-xs text-[#6B7280] mt-1">
              Kritik seviye: {fmtInt(product.critical_stock_level)} {product.unit}
            </div>
          </div>

          {canSeePrice && (
            <>
              <div className="azd-card p-4">
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
                  Alış Fiyatı
                </div>
                <div className="text-lg font-bold text-[#111827]">{fmtTL(product.purchase_price)}</div>
              </div>
              <div className="azd-card p-4">
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
                  Satış Fiyatı
                </div>
                <div className="text-lg font-bold text-[#0B3676]">{fmtTL(product.calculated_sales_price)}</div>
                <div className="text-[11px] text-[#6B7280] mt-1">
                  Min: {fmtTL(product.calculated_minimum_sales_price)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="azd-card p-5">
        <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">
          Stok Hareketleri ({movements.length})
        </div>
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Tarih</th>
                <th className="text-left px-3 py-2 font-semibold">Tip</th>
                <th className="text-right px-3 py-2 font-semibold">Miktar</th>
                <th className="text-right px-3 py-2 font-semibold">Önce</th>
                <th className="text-right px-3 py-2 font-semibold">Sonra</th>
                <th className="text-left px-3 py-2 font-semibold">Açıklama</th>
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-[#6B7280]">Hareket yok</td></tr>
              ) : (
                movements.map((m) => (
                  <tr key={m.id} className="border-t border-[#E5E7EB]">
                    <td className="px-3 py-2 text-xs text-[#6B7280]">{fmtDateTime(m.created_at)}</td>
                    <td className="px-3 py-2">
                      <Badge variant={m.direction === "in" ? "success" : "error"}>
                        {{
                          initial: "İlk Stok", production_in: "Üretim Girişi",
                          ready_purchase_in: "Hazır Alım", return_in: "İade",
                          count_excess: "Sayım+", manual_increase: "Manuel+",
                          sales_out: "Satış Çıkışı", sample: "Numune", demo: "Demo",
                          gift: "Hediye", count_deficit: "Sayım-", fire: "Fire",
                          manual_decrease: "Manuel-",
                        }[m.movement_type] || m.movement_type}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-bold">
                      <span className={m.direction === "in" ? "text-[#16A34A]" : "text-[#DC2626]"}>
                        {m.direction === "in" ? "+" : "-"}{fmtInt(m.quantity)} {m.unit}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-[#6B7280]">{fmtInt(m.previous_stock)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[#08285A]">{fmtInt(m.new_stock)}</td>
                    <td className="px-3 py-2 text-xs text-[#6B7280]">{m.description || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value, valueEl }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">{label}</div>
    {valueEl || <div className="font-semibold text-[#111827]">{value || "-"}</div>}
  </div>
);
